import { match } from '@/utils/pattern-match-util'
import DataLoader from 'dataloader'
import type {
  GetCurrentMemberFn,
  PrimeMemberByPublicIdFn,
  PrimeMemberFn,
  PrimeTeamFn,
  PrimeUserFn,
  PrimeWorkspaceFn,
} from '@/features/iam'
import { collection } from '@/infra/collection'
import type { PrimeConversationFn, PrimeRoomFn } from '@/features/messaging'
import { type Message } from '@/features/messaging/Message'
import { action } from 'mobx'
import { merge, type Observable, Subject } from 'rxjs'
import { ValueRetrievablePromise } from '@/utils/value-retrievable-promise'
import { concatMap, filter, map, share } from 'rxjs/operators'
import type { CursorPaged, CursorPageQuery } from '@/infra/paging'
import { Id } from '@/infra/id'
import { filterNonNil } from '@/utils/rx-util'
import { LegendVersionId } from '@/features/jobs/legends/PublishedLegend'
import { type PrimePublishedLegendFn } from '@/features/jobs/legends/PublishedLegendStore'
import { type JobsApi } from '@/features/jobs/api/job-api'
import {
  Job,
  type AmendmentVisibility,
  type CollaboratorProps,
  type JobProps,
} from './Job'
import { JobEvent, type JobEventProps } from './JobEvent'
import {
  mapAmendmentProtoToProps,
  mapBulkAdvanceJobsResponseProtoToResult,
  mapBulkArchiveJobsResponseProtoToResult,
  mapBulkCopyJobResponseProtoToResult,
  mapBulkRestoreJobsResponseProtoToResult,
  mapBulkTransferJobsResponseProtoToErrors,
  mapBillingTypeProtoToJobBillingType,
  mapCopyJobResponseProtoToResponse,
  mapJobEventProtoToProps,
  mapJobScopeItemProtoToProps,
  mapJobStatusProtoToJobStatus,
  mapJobTypeProtoToJobType,
  mapLegendServiceTypeProtoToJobLegendServiceType,
  mapMemberRoleProtoToCollaboratorRole,
  mapMemberTypeProtoToCollaboratorType,
  mapPaymentInfoStatusProtoToPaymentInfoStatus,
  mapPayoutSplitProtoToProps,
  mapRatingConfigProtoToProps,
  mapResolutionProtoToResolution,
  mapReturnAmountsYearProtoToProps,
  mapWaitingOnProtoToWaitingOn,
} from './job-mappers'
import {
  mapConsultationIncompleteOptionsToProto,
  mapConsultationProtoToProps,
} from '@/features/jobs/consultations/consultation-mappers'
import { synthesizedJobUpdates } from './synthesized-job-updates'
import {
  CloseJobRequest,
  ForfeitJobRequest,
  GetJobRequest,
  PlaceJobOnHoldRequest,
  RenameJobRequest,
  ResumeJobRequest,
  AdvanceJobRequest,
  AmendmentVisibility as AmendmentVisibilityProto,
  ClaimJobRequest,
  CopyJobRequest,
  CompleteInfoGatheringRequest,
  RequestJobChampionProviderRequest,
  RemoveSupportingClientRequest,
  AddTeamMemberAsSupportingClientRequest,
  BulkCopyJobRequest,
  RecordJobReturnAmountsRequest,
  JobReturnAmountsYear,
  JobReturnStateAmount,
  type Job as JobProto,
} from '@taxfyle/api-internal/internal/job_pb'
import {
  ISOToTimestamp,
  maybeDecimalToNumber,
  numberToDecimal,
  safeTimestampToDate,
  timestampToDate,
} from '@/utils/grpc-util'
import {
  AddChampionProviderRequest,
  AddSupportingProviderRequest,
  CancelJobRequest,
  CloseJobAsTestRequest,
  CompleteJobRequest,
  CreateAmendmentRequest,
  RemoveClientTeamFromJobRequest,
  RemoveJobCollaboratorRequest,
  ReopenJobRequest,
  TransferJobToClientRequest,
  TransferJobToClientTeamRequest,
  TransferJobToPoolRequest,
  TransferJobToProviderRequest,
  ManuallySplitJobPayoutRequest,
  Allocation,
  ArchiveJobRequest,
  RemoveCouponFromJobRequest,
  ApplyCouponToJobRequest,
  AddInvoiceRequest,
  OverrideDeadlineRequest,
  ChargeRequest,
  RefundRequest,
  AddSupportingClientRequest,
  NotifyProvidersOfOpenJobRequest,
  BulkNotifyProvidersOfOpenJobsRequest,
  BulkAdvanceJobsRequest,
  BulkArchiveJobsRequest,
  BulkRestoreJobsRequest,
  BulkTransferJobsToPoolRequest,
  BulkTransferJobsToClientTeamRequest,
  BulkTransferJobsToClientRequest,
  BulkTransferJobsToProviderRequest,
} from '@taxfyle/api-internal/internal/job_admin_pb'
import { type User } from '@/features/iam/users/User'
import { type FetchBillingTransactionsFn } from '@/features/billing'
import {
  CallCenterApplyCouponRequest,
  CallCenterRemoveCouponRequest,
} from '@taxfyle/api-internal/internal/call_center_pb'
import { StringValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import { type RpcError, StatusCode } from 'grpc-web'
import { RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'
import TypeCase = RealtimeEvent.TypeCase
import {
  ListJobEventsRequest,
  type JobEvent as JobEventProto,
} from '@taxfyle/api-internal/internal/job_event_pb'
import { isNil } from 'lodash-es'
import { type ConsultationIncompleteOptions } from '@/features/jobs/consultations/Consultation'
import type { FetchJobOptions, PickupResult, PrimeJobFn } from '@/features/jobs'
import type { BulkActionError } from '@/features/jobs/list/bulk-actions'

/**
 * A job that was created, changed or removed on the server.
 */
export interface JobChangeEvent {
  event: 'created' | 'patched' | 'removed'
  data: Job
}

/**
 * The draft that a copied job produced.
 */
export interface CopyJobResponse {
  jobId: Id
  readyForSubmission: boolean
}

/**
 * Copies several jobs at once.
 */
export type BulkCopyJobsFn = (jobIds: Id[]) => Promise<BulkCopyJobsResult>

/**
 * Outcome of copying several jobs at once.
 */
export interface BulkCopyJobsResult {
  /**
   * IDs of the copies that were created.
   */
  copiedJobIds: Id[]
  /**
   * The jobs that could not be copied.
   */
  errors: BulkCopyJobError[]
}

/**
 * A job that could not be copied.
 */
export interface BulkCopyJobError {
  jobId: Id
  error: string
}

/**
 * Outcome of advancing several pending jobs at once.
 */
export interface BulkAdvanceJobsResult {
  advancedIds: Id[]
  /**
   * The jobs that were not pending.
   */
  skippedIds: Id[]
  errors: BulkActionError[]
}

/**
 * Input for a single tax year's final return amounts. Amounts are signed:
 * positive = refund to client, negative = owed.
 */
export interface FinalReturnYearInput {
  taxYear: string
  federalAmount?: number
  stateAmounts: { state: string; amount: number }[]
}

/**
 * A job that the server reports as updated.
 */
export interface JobUpdatedEvent {
  type: 'JobUpdated'
  job: Job
}

/**
 * A payout allocation for one user.
 */
export interface AllocationInput {
  user: User
  allocationAmount: number
  authorizedPartialPayoutAmount: number
}

/**
 * Options for adding an invoice that was charged outside of the platform.
 */
export interface AddManualInvoiceOptions {
  jobId: Id
  invoiceId: string
  amount: number
  reason: string
  isDraft: boolean
}

/**
 * Options for charging a job.
 */
export interface ChargeJobOptions {
  jobId: Id
  paymentMethodId: Id
  amount: number
  reason: string
}

/**
 * Options for refunding a job.
 */
export interface RefundJobOptions {
  jobId: Id
  amount: number
  reason: string
}

/**
 * Options for creating an amendment.
 */
export interface CreateAmendmentOptions {
  jobId: Id
  description: string
  internalNote: string
  visibility: AmendmentVisibility
  clientAmount: number
  providerAmount: number
}

/**
 * Options for transferring a job back to the pool.
 */
export interface TransferJobOptions {
  jobId: Id
  reason: string
}

/**
 * Options for transferring several jobs back to the pool.
 */
export interface BulkTransferJobsOptions {
  jobIds: Id[]
  reason: string
}

/**
 * Options for transferring a job to a Client.
 */
export interface TransferJobToClientOptions {
  jobId: Id
  clientUserPublicId: Id
  reason: string
}

/**
 * Options for transferring several jobs to a Client.
 */
export interface BulkTransferJobsToClientOptions {
  jobIds: Id[]
  clientId: Id
  reason: string
}

/**
 * Options for transferring a job to a Pro.
 */
export interface TransferJobToProOptions {
  jobId: Id
  providerUserPublicId: Id
  reason: string
}

/**
 * Options for transferring several jobs to a Pro.
 */
export interface BulkTransferJobsToProOptions {
  jobIds: Id[]
  providerId: Id
  reason: string
}

/**
 * Options for transferring a job to a Client team.
 */
export interface TransferJobToClientTeamOptions {
  jobId: Id
  teamId: Id
  reason: string
}

/**
 * Options for transferring several jobs to a Client team.
 */
export interface BulkTransferJobsToClientTeamOptions {
  jobIds: Id[]
  teamId: Id
  reason: string
}

/**
 * Options for adding a Pro to a job.
 */
export interface AddProviderOptions {
  jobId: Id
  providerUserPublicId: Id
  reason: string
}

/**
 * Options for adding a supporting Client to a job.
 */
export interface AddSupportingClientOptions {
  jobId: Id
  clientUserPublicId: Id
  reason: string
}

/**
 * Options for removing a collaborator from a job.
 */
export interface RemoveCollaboratorOptions {
  jobId: Id
  collaboratorUserPublicId: Id
  reason: string
}

/**
 * Options for completing, cancelling or closing a job as a test.
 */
export interface ResolveJobOptions {
  jobId: Id
  reason: string
  doBilling: boolean
  billingMethodId: Id
}

/**
 * Job Store.
 */
export class JobStore {
  /**
   * Jobs collection.
   */
  readonly jobs = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new Job(),
  })

  /**
   * Job events collection. We keep it here rather than
   * on the job because Job Event will attempt to hydrate a job.
   */
  readonly jobEvents = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new JobEvent(),
  })

  private readonly _jobLoader = new DataLoader(this._fetchJobs.bind(this))

  /**
   * Get a single job by ID from the local collection.
   */
  readonly getJob = this.jobs.get

  /**
   * Primes a job.
   */
  readonly primeJob: PrimeJobFn = (props) => this.jobs.set(props)

  /**
   * Job updates observable. Emits an event every time the server tells
   * us about updates.
   */
  readonly jobUpdates$

  /**
   * JobEvents observable. When realtime JobEvents are emitted, they flow through here.
   */
  readonly jobEvents$

  /**
   * Local job changes subject.
   */
  private readonly _localJobChangesSubject = new Subject<Job>()

  /**
   * Emits jobs that have been updated by actions taken locally.
   */
  readonly localJobChanges$

  /**
   * Emits job updated events from Dotnet API.
   */
  readonly jobUpdatedEvents$: Observable<JobUpdatedEvent>

  constructor(
    private readonly _api: JobsApi,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _primeConversation: PromiseLike<PrimeConversationFn>,
    private readonly _primeWorkspace: PrimeWorkspaceFn,
    private readonly _primeUser: PrimeUserFn,
    private readonly _primeMember: PrimeMemberFn,
    private readonly _primeTeam: PrimeTeamFn,
    private readonly _primePublishedLegend: PrimePublishedLegendFn,
    private readonly _newMessages$: Observable<Message>,
    private readonly _fetchBillingTransactions: FetchBillingTransactionsFn,
    private readonly _events$: Observable<RealtimeEvent>,
    private readonly _primeMemberByPublicId: PrimeMemberByPublicIdFn,
    private readonly _primeRoom: PromiseLike<PrimeRoomFn>,
  ) {
    this.jobUpdates$ = merge(
      this._events$.pipe(
        filter(
          (event) =>
            event.getTypeCase() === TypeCase.JOB_CREATED ||
            event.getTypeCase() === TypeCase.JOB_UPDATED,
        ),
        concatMap((realtimeEvent) =>
          this._handleRemoteJobUpdate(realtimeEvent),
        ),
        filterNonNil(),
      ),
      synthesizedJobUpdates(this._newMessages$, this.fetchJob.bind(this)),
    ).pipe(share())

    this.jobEvents$ = this._events$.pipe(
      map(this._handleRemoteJobEventRecorded),
      filterNonNil(),
      map((props) => this.jobEvents.set(props)),
      share(),
    )

    this.localJobChanges$ = this._localJobChangesSubject.asObservable()

    this.jobUpdatedEvents$ = this._events$.pipe(
      filter((event) => event.getTypeCase() === TypeCase.JOB_UPDATED),
      map((e) => this._mapJobUpdatedRealtimeEvent(e)),
      filterNonNil(),
      share(),
    )

    this.fetchJob = this.fetchJob.bind(this)
    this.fetchJobs = this.fetchJobs.bind(this)
    this.pickup = this.pickup.bind(this)
    merge(this.jobUpdates$, this.jobEvents$, this.jobUpdatedEvents$).subscribe()
    // Clear the loader cache and in-mem model when a Job is removed.
    this.jobUpdates$
      .pipe(filter((e) => e.event === 'removed'))
      .subscribe((e) => {
        this.jobs.remove(e.data.id!)
        this._jobLoader.clear(e.data.id!)
      })
  }

  /**
   * Looks up events by Job.
   */
  readonly eventsForJob = (job: Job) =>
    this.jobEvents.filter((e) => e.job === job)

  /**
   * Fetches a job, reusing the cached one unless `force` is set.
   */
  async fetchJob(
    id: Id,
    options?: FetchJobOptions,
    signal?: AbortSignal,
  ): Promise<Job> {
    const assertedId = Id.assert(id)
    if (options?.force) {
      this._jobLoader.clear(assertedId)
    }

    const job = await this._jobLoader.load(assertedId)
    signal?.throwIfAborted()
    return job
  }

  /**
   * Fetch many jobs
   */
  async fetchJobs(ids: Id[], signal?: AbortSignal): Promise<Job[]> {
    const assertedIds = ids.map(Id.assert)
    const jobs = await Promise.all(
      assertedIds.map((id) => this._jobLoader.load(id)),
    )
    signal?.throwIfAborted()
    return jobs
  }

  /**
   * Lists job events.
   */
  async listJobEvents(
    jobId: Id,
    paging: CursorPageQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<JobEvent>> {
    const request = new ListJobEventsRequest().setJobId(Id.assert(jobId))
    request.setPageSize(paging.limit)
    if (paging.after) {
      request.setPageToken(paging.after)
    }

    const result = await this._api.jobEvents.listJobEvents(request)
    signal?.throwIfAborted()
    return {
      data: this.jobEvents.set(
        result.getJobEventsList().map((x) => this._mapJobEventProtoToProps(x)),
      ),
      next: result.getNextPageToken(),
    }
  }

  /**
   * Notifies pros of the availability of the given job.
   */
  async notifyProsOfOpenJob(job: Job) {
    await this._api.jobsAdmin.notifyProvidersOfOpenJob(
      new NotifyProvidersOfOpenJobRequest().setId(Id.assert(job.id)),
    )
  }

  /**
   * Bulk notifies pros of all unclaimed jobs in a workspace.
   */
  async bulkNotifyProsOfOpenJobs(workspaceId: Id) {
    const response = await this._api.jobsAdmin.bulkNotifyProvidersOfOpenJobs(
      new BulkNotifyProvidersOfOpenJobsRequest().setWorkspaceId(
        Id.assert(workspaceId),
      ),
    )
    return response.getJobsNotified()
  }

  /**
   * Picks up a job by its ID.
   */
  async pickup(jobId: Id): Promise<PickupResult> {
    const claimJobRequest = new ClaimJobRequest().setId(Id.assert(jobId))

    try {
      await this._api.jobs.claimJob(claimJobRequest)
      const job = await this._fetchJob(Id.assert(jobId))
      this._localJobChangesSubject.next(job)
      return 'Success'
    } catch (error) {
      const rpcError = error as RpcError
      if (rpcError.code === StatusCode.FAILED_PRECONDITION) {
        if (rpcError.message.includes('JobAlreadyPickedUp')) {
          return 'Vanished'
        }
      }

      throw error
    }
  }

  /**
   * Copies a job into a new draft.
   */
  async copyJob(job: Job): Promise<CopyJobResponse> {
    const request = new CopyJobRequest().setId(Id.assert(job.id))
    const response = await this._api.jobs.copyJob(request)
    return mapCopyJobResponseProtoToResponse(response)
  }

  /**
   * Copies several jobs into new drafts.
   */
  async bulkCopyJobs(jobIds: Id[]): Promise<BulkCopyJobsResult> {
    const request = new BulkCopyJobRequest().setIdsList(
      jobIds.map((id) => Id.assert(id)),
    )
    const response = await this._api.jobs.bulkCopyJob(request)
    return mapBulkCopyJobResponseProtoToResult(response)
  }

  /**
   * Complete the info gathering step for a job
   */
  async completeInfoGathering(jobId: Id, completeAsAdmin = false) {
    const request = new CompleteInfoGatheringRequest()
      .setId(Id.assert(jobId))
      .setCompleteAsAdmin(completeAsAdmin)
    await this._api.jobs.completeInfoGathering(request)
    await this._fetchJob(Id.assert(jobId))
  }

  /**
   * Renames a job.
   *
   * @param job
   * @param newName
   */
  async rename(job: Job, newName: string) {
    const req = new RenameJobRequest().setId(Id.assert(job.id)).setName(newName)

    const response = await this._api.jobs.renameJob(req)

    const updatedJob = this.jobs.set({
      id: job.id,
      name: response.getName() || job.description,
      description: job.description,
      dateModified: timestampToDate(response.getUpdateTime()),
    })

    this._localJobChangesSubject.next(updatedJob)

    return updatedJob
  }

  /**
   * Places a job on hold.
   */
  async placeJobOnHold(jobId: Id) {
    const id = Id.assert(jobId)
    const request = new PlaceJobOnHoldRequest().setId(id)
    const response = await this._api.jobs.placeJobOnHold(request)
    return this.jobs.set({
      id,
      status: mapJobStatusProtoToJobStatus(response.getStatus()),
      dateModified: timestampToDate(response.getUpdateTime()),
    })
  }

  /**
   * Records Pro-entered per-year final return amounts (signed federal + per-state
   * amounts). Positive = refund to client, negative = owed.
   */
  async recordJobReturnAmounts(jobId: Id, years: FinalReturnYearInput[]) {
    const request = new RecordJobReturnAmountsRequest().setJobId(
      Id.assert(jobId),
    )
    request.setYearsList(
      years.map((y) => {
        const yearInput = new JobReturnAmountsYear().setTaxYear(y.taxYear)
        if (y.federalAmount !== undefined) {
          yearInput.setFederalAmount(numberToDecimal(y.federalAmount))
        }
        yearInput.setStateAmountsList(
          y.stateAmounts.map((s) =>
            new JobReturnStateAmount()
              .setState(s.state)
              .setAmount(numberToDecimal(s.amount)),
          ),
        )
        return yearInput
      }),
    )
    await this._api.jobs.recordJobReturnAmounts(request)
  }

  /**
   * Archives a job as an Admin.
   */
  async deleteJobAsAdmin(job: Job) {
    const jobId = Id.assert(job.id)

    const request = new ArchiveJobRequest().setId(jobId)

    await this._api.jobsAdmin.archiveJob(request)

    this.jobs.remove(jobId)
  }

  /**
   * Adds an invoice that was charged outside of the platform.
   */
  async addManualInvoice({
    jobId,
    invoiceId,
    amount,
    reason,
    isDraft,
  }: AddManualInvoiceOptions) {
    const request = new AddInvoiceRequest()
      .setId(Id.assert(jobId))
      .setChargeTransactionId(invoiceId)
      .setAmount(numberToDecimal(amount))
      .setReason(reason)

    if (isDraft) {
      await this._api.draftsAdmin.addInvoice(request)
    } else {
      await this._api.jobsAdmin.addInvoice(request)
    }

    await this._fetchJob(Id.assert(jobId))
  }

  /**
   * Charges a job with the specified payment method and amount.
   */
  async chargeJob({
    jobId,
    paymentMethodId,
    amount,
    reason,
  }: ChargeJobOptions) {
    const request = new ChargeRequest()
      .setId(Id.assert(jobId))
      .setAmount(numberToDecimal(amount))
      .setReason(reason)
      .setBillingMethodId(Id.assert(paymentMethodId))

    await this._api.jobsAdmin.chargeJob(request)

    const updated = await this._fetchJob(Id.assert(jobId))
    await this._fetchBillingTransactions(updated.stripeCharges)
  }

  /**
   * Refunds a job with the specified amount.
   */
  async refundJob({ jobId, amount, reason }: RefundJobOptions) {
    const request = new RefundRequest()
      .setId(Id.assert(jobId))
      .setAmount(numberToDecimal(amount))
      .setReason(reason)

    await this._api.jobsAdmin.refundJob(request)

    const updated = await this._fetchJob(Id.assert(jobId))
    await this._fetchBillingTransactions([
      ...updated.stripeCharges,
      ...updated.stripeRefunds,
    ])
  }

  /**
   * Overrides a job's deadline.
   */
  async updateDeadline(jobId: Id, deadline: Date) {
    const request = new OverrideDeadlineRequest()
      .setJobId(Id.assert(jobId))
      .setDeadline(ISOToTimestamp(deadline.toISOString()))

    await this._api.jobsAdmin.overrideDeadline(request)

    await this._fetchJob(Id.assert(jobId))
  }

  /**
   * Resumes a job that is on hold.
   */
  async resumeJob(jobId: Id) {
    const id = Id.assert(jobId)
    const request = new ResumeJobRequest().setId(id)
    const response = await this._api.jobs.resumeJob(request)
    return this.jobs.set({
      id,
      status: mapJobStatusProtoToJobStatus(response.getStatus()),
      dateModified: timestampToDate(response.getUpdateTime()),
    })
  }

  /**
   * Advances a pending job.
   */
  async advanceJob(jobId: Id) {
    const id = Id.assert(jobId)
    const request = new AdvanceJobRequest().setId(id)
    const response = await this._api.jobs.advanceJob(request)
    return this.jobs.set({
      id,
      status: mapJobStatusProtoToJobStatus(response.getStatus()),
      dateModified: timestampToDate(response.getUpdateTime()),
    })
  }

  /**
   * Advances several pending jobs, and refetches the advanced ones so that
   * lists update them in place.
   */
  async bulkAdvanceJobs(jobIds: Id[]): Promise<BulkAdvanceJobsResult> {
    const ids = jobIds.map(Id.assert)
    const response = await this._api.jobsAdmin.bulkAdvanceJobs(
      new BulkAdvanceJobsRequest().setIdsList(ids),
    )
    const result = mapBulkAdvanceJobsResponseProtoToResult(ids, response)

    const advancedJobs = await this._fetchJobs(
      result.advancedIds.map(Id.assert),
    )
    advancedJobs.forEach((job) => this._localJobChangesSubject.next(job))

    return result
  }

  /**
   * Creates an amendment to the job.
   */
  createAmendment({
    jobId,
    description,
    internalNote,
    visibility,
    clientAmount,
    providerAmount,
  }: CreateAmendmentOptions) {
    const id = Id.assert(jobId)
    const request = new CreateAmendmentRequest()
      .setId(id)
      .setDescription(description)
      .setInternalNote(internalNote)
      .setVisibility(
        match(visibility, {
          Public: () => AmendmentVisibilityProto.PUBLIC,
          Internal: () => AmendmentVisibilityProto.INTERNAL,
        }),
      )
      .setCustomerAmount(numberToDecimal(clientAmount))
      .setProviderAmount(numberToDecimal(providerAmount))

    return this._api.jobsAdmin
      .createAmendment(request)
      .then(() => this._updateJobEvents(id))
  }

  /**
   * Transfers a job back to the pool as an Admin.
   */
  async transferJobToPoolAsAdmin({ jobId, reason }: TransferJobOptions) {
    const id = Id.assert(jobId)
    const request = new TransferJobToPoolRequest().setId(id).setReason(reason)

    return this._api.jobsAdmin
      .transferJobToPool(request)
      .then(() => this._updateJobEvents(id))
  }

  /**
   * Transfers several jobs back to the pool.
   */
  async bulkTransferJobsToPool({ jobIds, reason }: BulkTransferJobsOptions) {
    const ids = jobIds.map(Id.assert)
    const request = new BulkTransferJobsToPoolRequest()
      .setIdsList(ids)
      .setReason(reason)

    const response = await this._api.jobsAdmin.bulkTransferJobsToPool(request)

    return mapBulkTransferJobsResponseProtoToErrors(response)
  }

  /**
   * Transfers a job to a Client as an Admin.
   */
  transferJobToClientAsAdmin({
    jobId,
    clientUserPublicId,
    reason,
  }: TransferJobToClientOptions) {
    const id = Id.assert(jobId)
    const request = new TransferJobToClientRequest()
      .setId(id)
      .setClientUserPublicId(Id.assert(clientUserPublicId))
      .setReason(reason)

    return this._api.jobsAdmin
      .transferJobToClient(request)
      .then(() => this._updateJobEvents(id))
  }

  /**
   * Transfers several jobs to a Client as an Admin.
   */
  async bulkTransferJobsToClientAsAdmin({
    jobIds,
    clientId,
    reason,
  }: BulkTransferJobsToClientOptions) {
    const ids = jobIds.map(Id.assert)
    const request = new BulkTransferJobsToClientRequest()
      .setIdsList(ids)
      .setClientUserPublicId(Id.assert(clientId))
      .setReason(reason)

    const response = await this._api.jobsAdmin.bulkTransferJobsToClient(request)

    return mapBulkTransferJobsResponseProtoToErrors(response)
  }

  /**
   * Transfers a job to a Pro as an Admin.
   */
  transferJobToProAsAdmin({
    jobId,
    providerUserPublicId,
    reason,
  }: TransferJobToProOptions) {
    const id = Id.assert(jobId)
    const request = new TransferJobToProviderRequest()
      .setId(id)
      .setProviderUserPublicId(Id.assert(providerUserPublicId))
      .setReason(reason)

    return this._api.jobsAdmin
      .transferJobToProvider(request)
      .then(() => this._updateJobEvents(id))
  }

  /**
   * Transfers several jobs to a Pro as an Admin.
   */
  async bulkTransferJobsToProviderAsAdmin({
    jobIds,
    providerId,
    reason,
  }: BulkTransferJobsToProOptions) {
    const ids = jobIds.map(Id.assert)
    const request = new BulkTransferJobsToProviderRequest()
      .setIdsList(ids)
      .setProviderUserPublicId(Id.assert(providerId))
      .setReason(reason)

    const response =
      await this._api.jobsAdmin.bulkTransferJobsToProvider(request)

    return mapBulkTransferJobsResponseProtoToErrors(response)
  }

  /**
   * Transfers several jobs to a Client team as an Admin.
   */
  async bulkTransferJobToClientTeamAsAdmin({
    jobIds,
    teamId,
    reason,
  }: BulkTransferJobsToClientTeamOptions) {
    const ids = jobIds.map(Id.assert)
    const request = new BulkTransferJobsToClientTeamRequest()
      .setIdsList(ids)
      .setTeamId(Id.assert(teamId))
      .setReason(reason)

    const response =
      await this._api.jobsAdmin.bulkTransferJobsToClientTeam(request)

    return mapBulkTransferJobsResponseProtoToErrors(response)
  }

  /**
   * Transfers a job to a Client team as an Admin.
   */
  transferJobToClientTeamAsAdmin({
    jobId,
    teamId,
    reason,
  }: TransferJobToClientTeamOptions) {
    const id = Id.assert(jobId)
    const request = new TransferJobToClientTeamRequest()
      .setId(id)
      .setTeamId(Id.assert(teamId))
      .setReason(reason)

    return this._api.jobsAdmin
      .transferJobToClientTeam(request)
      .then(() => this._updateJobEvents(id))
  }

  /**
   * Requests a champion Pro to a job as Admin.
   *
   * @param id
   * @param championProviderId
   */
  async requestChampionProviderAsAdmin(id: Id, championProviderId: Id | null) {
    const request = new RequestJobChampionProviderRequest().setId(Id.assert(id))

    if (championProviderId) {
      const championProviderIdValue = new StringValue()
      championProviderIdValue.setValue(Id.assert(championProviderId))

      request.setProviderUserPublicId(championProviderIdValue)
    }

    return this._api.jobsAdmin
      .requestChampionProvider(request)
      .then(() => this._updateJobEvents(Id.assert(id)))
  }

  /**
   * Adds a champion Provider to a job as Admin.
   */
  addChampionProviderAsAdmin({
    jobId,
    providerUserPublicId,
    reason,
  }: AddProviderOptions) {
    const id = Id.assert(jobId)
    const request = new AddChampionProviderRequest()
      .setId(id)
      .setProviderUserPublicId(Id.assert(providerUserPublicId))
      .setReason(reason)

    return this._api.jobsAdmin
      .addChampionProvider(request)
      .then(() => this._updateJobEvents(id))
  }

  /**
   * Adds a supporting Provider to a job as Admin.
   */
  addSupportingProviderAsAdmin({
    jobId,
    providerUserPublicId,
    reason,
  }: AddProviderOptions) {
    const id = Id.assert(jobId)
    const request = new AddSupportingProviderRequest()
      .setId(id)
      .setProviderUserPublicId(Id.assert(providerUserPublicId))
      .setReason(reason)

    return this._api.jobsAdmin
      .addSupportingProvider(request)
      .then(() => this._updateJobEvents(id))
  }

  /**
   * Adds a supporting Client to a job as Admin.
   */
  addSupportingClientAsAdmin({
    jobId,
    clientUserPublicId,
    reason,
  }: AddSupportingClientOptions) {
    const id = Id.assert(jobId)
    const request = new AddSupportingClientRequest()
      .setId(id)
      .setClientUserPublicId(Id.assert(clientUserPublicId))
      .setReason(reason)

    return this._api.jobsAdmin
      .addSupportingClient(request)
      .then(() => this._updateJobEvents(id))
  }

  /**
   * Reopens a job as an Admin.
   *
   * @param jobId
   * @param reason
   */
  async reopenJobAsAdmin(jobId: Id, reason: string) {
    const id = Id.assert(jobId)
    const request = new ReopenJobRequest().setId(id).setReason(reason)

    return this._api.jobsAdmin
      .reopenJob(request)
      .then(() => this._updateJobEvents(id))
  }

  /**
   * Removes client team from Job as Admin.
   *
   * @param jobId
   * @param reason
   */
  async removeClientTeamAsAdmin(jobId: Id, reason: string) {
    const id = Id.assert(jobId)
    const request = new RemoveClientTeamFromJobRequest()
      .setId(id)
      .setReason(reason)

    await this._api.jobsAdmin.removeClientTeamFromJob(request)

    await Promise.all([this._fetchJob(id), this._updateJobEvents(id)])
  }

  /**
   * Removes a collaborator from a job as Admin.
   */
  async removeCollaboratorAsAdmin({
    jobId,
    collaboratorUserPublicId,
    reason,
  }: RemoveCollaboratorOptions) {
    const id = Id.assert(jobId)
    const request = new RemoveJobCollaboratorRequest()
      .setId(id)
      .setCollaboratorUserPublicId(Id.assert(collaboratorUserPublicId))
      .setReason(reason)

    await this._api.jobsAdmin.removeJobCollaborator(request)

    return this._fetchJob(id)
  }

  /**
   * Removes a supporting client from a job as either champion client or team member.
   *
   * @param jobId
   * @param collaboratorUserPublicId
   */
  async removeClientAsNonAdmin(jobId: Id, collaboratorUserPublicId: Id) {
    const id = Id.assert(jobId)
    const request = new RemoveSupportingClientRequest()
      .setId(id)
      .setClientUserPublicId(Id.assert(collaboratorUserPublicId))

    await this._api.jobs.removeSupportingClient(request)
    return this._fetchJob(id)
  }

  /**
   * Add a team member as a supporting client to the job.
   *
   * @param jobId
   * @param userPublicId
   */
  async addTeamMemberAsSupportingClient(jobId: Id, userPublicId: Id) {
    const id = Id.assert(jobId)
    const request = new AddTeamMemberAsSupportingClientRequest()
      .setId(id)
      .setUserPublicId(Id.assert(userPublicId))

    await this._api.jobs.addTeamMemberAsSupportingClient(request)
    return this._fetchJob(id)
  }

  /**
   * Closes a job.
   *
   * @param job
   * @param consultationIncomplete
   */
  async close(
    job: Job,
    consultationIncomplete?: ConsultationIncompleteOptions,
  ) {
    const request = new CloseJobRequest().setId(Id.assert(job.id))

    if (consultationIncomplete) {
      request.setConsultationIncomplete(
        mapConsultationIncompleteOptionsToProto(consultationIncomplete),
      )
    }

    await this._api.jobs.closeJob(request)

    const updated = await this._fetchJob(Id.assert(job.id))

    await this._fetchBillingTransactions(updated.stripeCharges)

    return updated
  }

  /**
   * Completes a job.
   */
  async complete({
    jobId,
    reason,
    doBilling,
    billingMethodId,
  }: ResolveJobOptions) {
    const id = Id.assert(jobId)
    const request = new CompleteJobRequest()
      .setId(id)
      .setReason(reason)
      .setDoBilling(doBilling)
      .setBillingMethodId(Id.assert(billingMethodId))

    await this._api.jobsAdmin
      .completeJob(request)
      .then(() => this._updateJobEvents(id))

    const updated = await this._fetchJob(id)

    await this._fetchBillingTransactions(updated.stripeCharges)

    return updated
  }

  /**
   * Cancels a job.
   */
  async cancel({
    jobId,
    reason,
    doBilling,
    billingMethodId,
  }: ResolveJobOptions) {
    const id = Id.assert(jobId)
    const request = new CancelJobRequest()
      .setId(id)
      .setReason(reason)
      .setDoBilling(doBilling)
      .setBillingMethodId(Id.assert(billingMethodId))

    return this._api.jobsAdmin
      .cancelJob(request)
      .then(() => this._updateJobEvents(id))
  }

  /**
   * Closes as a Test job.
   */
  closeJobAsTest({
    jobId,
    reason,
    doBilling,
    billingMethodId,
  }: ResolveJobOptions) {
    const id = Id.assert(jobId)
    const request = new CloseJobAsTestRequest()
      .setId(id)
      .setReason(reason)
      .setDoBilling(doBilling)
      .setBillingMethodId(Id.assert(billingMethodId))

    return this._api.jobsAdmin
      .closeJobAsTest(request)
      .then(() => this._updateJobEvents(id))
  }

  /**
   * Forfeits the job.
   *
   * @param job
   */
  async forfeit(job: Job) {
    const req = new ForfeitJobRequest().setId(Id.assert(job.id))

    await this._api.jobs.forfeitJob(req)

    return this._fetchJob(Id.assert(job.id))
  }

  /**
   * Applies a coupon as an admin to a job.
   *
   * @param job
   * @param couponId
   */
  async applyCouponAsAdmin(job: Job, couponId: Id) {
    const request = new ApplyCouponToJobRequest()
      .setId(Id.assert(job.id))
      .setCouponId(Id.assert(couponId))

    await this._api.jobsAdmin.applyCoupon(request)

    await this._fetchJob(Id.assert(job.id))
  }

  /**
   * Applies a coupon to a draft by its code, on behalf of the call center.
   */
  async callCenterApplyCouponByCode(jobId: Id, couponCode: string) {
    const request = new CallCenterApplyCouponRequest()
      .setCouponCode(couponCode)
      .setDraftId(Id.assert(jobId))

    await this._api.callCenter.applyCoupon(request)

    await this._fetchJob(Id.assert(jobId))
  }

  /**
   * Removes the coupon from a draft, on behalf of the call center.
   */
  async callCenterRemoveCouponByCode(jobId: Id) {
    const request = new CallCenterRemoveCouponRequest().setDraftId(
      Id.assert(jobId),
    )

    await this._api.callCenter.removeCoupon(request)

    await this._fetchJob(Id.assert(jobId))
  }

  /**
   * Removes coupon as an admin from a job.
   *
   * @param job
   */
  async removeCouponAsAdmin(job: Job) {
    const request = new RemoveCouponFromJobRequest().setId(Id.assert(job.id))

    await this._api.jobsAdmin.removeCoupon(request)

    await this._fetchJob(Id.assert(job.id))
  }

  /**
   * Splits the payout.
   *
   * @param job
   * @param allocations
   */
  async splitPayout(job: Job, allocations: AllocationInput[]) {
    const id = Id.assert(job.id)
    const allocationProtos = allocations.map((input) =>
      new Allocation()
        .setUserPublicId(Id.assert(input.user.publicId))
        .setAmount(numberToDecimal(input.allocationAmount))
        .setAuthorizedPartialPayoutAmount(
          numberToDecimal(input.authorizedPartialPayoutAmount),
        ),
    )
    const request = new ManuallySplitJobPayoutRequest()
      .setId(id)
      .setAllocationsList(allocationProtos)

    await this._api.jobsAdmin.manuallySplitJobPayout(request)

    await this._fetchJobs([id])
  }

  /**
   * Attempts to archive the given jobs.
   */
  async archiveJobs(jobIds: Id[]) {
    const ids = jobIds.map(Id.assert)

    const response = await this._api.jobsAdmin.bulkArchive(
      new BulkArchiveJobsRequest().setIdsList(ids),
    )

    return mapBulkArchiveJobsResponseProtoToResult(ids, response)
  }

  /**
   * Attempts to restore (meaning unarchive) the given jobs.
   */
  async restoreJobs(jobIds: Id[]) {
    const ids = jobIds.map(Id.assert)

    const response = await this._api.jobsAdmin.bulkRestore(
      new BulkRestoreJobsRequest().setIdsList(ids),
    )

    return mapBulkRestoreJobsResponseProtoToResult(ids, response)
  }

  /**
   * Maps the proto message to an event type.
   *
   * @param e
   */
  private _mapJobUpdatedRealtimeEvent(
    e: RealtimeEvent,
  ): JobUpdatedEvent | null {
    if (e.getTypeCase() !== TypeCase.JOB_UPDATED) {
      return null
    }

    const proto = e.getJobUpdated()?.getJob()
    if (!proto) {
      return null
    }

    // Dotnet API events are over-exposing information, so we can't use all
    // the data that's on there.
    // This is currently only used for the Insights feature which relies on
    // a signal for the job to have updated, so we'll just update the
    // Last Activity for now.
    const existing = this.jobs.get(proto.getId())
    if (!existing) {
      return null
    }

    return {
      type: 'JobUpdated',
      job: this.jobs.set({
        id: proto.getId(),
        dateLastActivity: timestampToDate(proto.getDateLastActivity()),
      }),
    }
  }

  private readonly _setJobProto = async (proto: JobProto): Promise<Job> => {
    // Resolved here rather than at construction so loading jobs doesn't load messaging.
    const { primeConversation, primeRoom } =
      await ValueRetrievablePromise.props({
        primeConversation: this._primeConversation,
        primeRoom: this._primeRoom,
      })
    return this.jobs.set(
      this._mapJobProtoToProps(proto, primeConversation, primeRoom),
    )
  }

  private _mapJobProtoToProps(
    proto: JobProto,
    primeConversation: PrimeConversationFn,
    primeRoom: PrimeRoomFn,
  ): Partial<JobProps> {
    const workspaceId = proto.getWorkspaceId()
    const ownerTeamId = proto.getOwnerTeamId()?.getValue()
    const legend = proto.getLegend()
    const billingConfig = legend?.getBillingConfig()
    const ratingConfig = legend?.getRatingConfig()
    const requestedProId = proto.getRequestedProId()?.getValue() ?? null
    const requestedProPublicId =
      proto.getRequestedProPublicId()?.getValue() ?? null
    const description = proto.getDescription()?.getValue()
    const couponAmount = maybeDecimalToNumber(proto.getCouponAmount())
    const conversationId = proto.getConversationId()?.getValue()
    const waitingOn = mapWaitingOnProtoToWaitingOn(proto.getWaitingOn())
    const payoutSplit = proto.getPayoutSplit()
    const consultation = proto.getConsultation()
    const jobType = proto.getType()

    let legendVersionId: LegendVersionId | undefined
    if (legend?.getId()) {
      legendVersionId = LegendVersionId(legend.getId(), legend.getVersion())
      this._primePublishedLegend(legendVersionId)
    }

    return {
      id: proto.getId(),
      shortId: proto.getShortId(),
      workspace: workspaceId
        ? this._primeWorkspace({ id: workspaceId })
        : undefined,
      ownerTeam: ownerTeamId ? this._primeTeam({ id: ownerTeamId }) : undefined,
      teams: proto.getTeamIdsList().map((id) => this._primeTeam({ id })),
      collaborators: proto
        .getMembersList()
        .map((m) => this._mapJobMemberProtoToProps(m, workspaceId)),
      legendVersionId,
      legendId: legend?.getId(),
      legendName: legend?.getName(),
      legendServiceType: legend
        ? mapLegendServiceTypeProtoToJobLegendServiceType(
            legend.getServiceType(),
          )
        : undefined,
      // Deliberately ignores the legend's price unit and skip-billing flag:
      // honouring them changes price formatting and billing for every job, which
      // needs its own rollout.
      legendPriceUnit: legend ? null : undefined,
      billingEnabled: legend ? true : undefined,
      deferPaymentInfoCollection: legend
        ? Boolean(billingConfig?.getDeferPaymentInfoCollection())
        : undefined,
      ratingConfig: ratingConfig
        ? mapRatingConfigProtoToProps(ratingConfig)
        : undefined,
      allowScheduling: legend?.getAllowScheduling(),
      alwaysEnableVoiceCall: legend?.getAlwaysEnableVoiceCall(),
      allowProSelection: legend?.getAllowProSelection(),
      paidAmount: maybeDecimalToNumber(proto.getPaidAmount()),
      total: maybeDecimalToNumber(proto.getTotal()),
      requestedProId,
      requestedProPublicId,
      requestedPro:
        !isNil(requestedProId) && !isNil(requestedProPublicId)
          ? this._primeMemberByPublicId({
              workspaceId,
              userPublicId: requestedProPublicId,
            })
          : requestedProId === null
            ? null
            : undefined,
      dateDeadline: timestampToDate(proto.getDateDeadline()),
      dateCreated: timestampToDate(proto.getDateCreated()),
      dateModified: timestampToDate(proto.getDateModified()),
      resolution: mapResolutionProtoToResolution(proto.getResolution()),
      dateSubmitted: timestampToDate(proto.getDateTransmitted()),
      dateAccepted: timestampToDate(proto.getDateAccepted()),
      dateClosed: timestampToDate(proto.getDateClosed()),
      dateAdvanceAllowed: timestampToDate(proto.getAdvanceAllowedTime()),
      dateLastActivity: timestampToDate(proto.getDateLastActivity()),
      answers: proto.getAnswersList().map((a) => ({
        id: a.getId(),
        answer: JSON.parse(a.getValue()) as unknown,
      })),
      paymentInfoStatus: mapPaymentInfoStatusProtoToPaymentInfoStatus(
        proto.getPaymentInfoStatus(),
      ),
      stripeCharges: proto.getStripeChargesList(),
      stripeRefunds: proto.getStripeRefundsList(),
      name: proto.getName()?.getValue() || description,
      description,
      primaryAmount: maybeDecimalToNumber(proto.getPrimaryAmount()),
      providerCutAmount: maybeDecimalToNumber(proto.getProviderCutAmount()),
      couponCodes: proto.getCouponCodesList().join(', '),
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- A zero coupon amount is treated as no coupon.
      couponAmount: couponAmount ? couponAmount : undefined,
      status: mapJobStatusProtoToJobStatus(proto.getStatus()),
      infoLines: proto.getInfoLinesList().map((l) => ({
        title: l.getTitle(),
        content: l.getContent(),
      })),
      returnAmounts: proto
        .getReturnAmountsByYearList()
        .map(mapReturnAmountsYearProtoToProps),
      conversation: conversationId
        ? primeConversation({ id: conversationId })
        : undefined,
      waitingOn,
      payoutSplit: payoutSplit
        ? mapPayoutSplitProtoToProps(payoutSplit, this._primeUser)
        : undefined,
      currentUserAllocation: maybeDecimalToNumber(
        proto.getCallingActorAllocation(),
      ),
      amendments: proto.getAmendmentsList().map(mapAmendmentProtoToProps),
      jobScopeItems: proto
        .getJobScopeItemsList()
        .map(mapJobScopeItemProtoToProps),
      billingType: mapBillingTypeProtoToJobBillingType(proto.getBillingType()),
      consultation: consultation
        ? mapConsultationProtoToProps(consultation, primeRoom)
        : undefined,
      jobType: jobType
        ? mapJobTypeProtoToJobType(jobType)
        : { type: 'Unspecified' },
    }
  }

  private _mapJobMemberProtoToProps(
    proto: JobProto.Member,
    workspaceId: string,
  ): CollaboratorProps {
    const teamId = proto.getTeamId()
    return {
      member: this._primeMemberByPublicId({
        workspaceId,
        userPublicId: proto.getId(),
        userId: proto.getUserId(),
        displayName: proto.getDisplayName(),
        email: proto.getEmail(),
        givenName: proto.getGivenName(),
        familyName: proto.getFamilyName(),
        picture: proto.getPicture(),
      }),
      type: mapMemberTypeProtoToCollaboratorType(proto.getType()),
      role: mapMemberRoleProtoToCollaboratorRole(proto.getRole()),
      team: teamId ? this._primeTeam({ id: teamId }) : null,
    }
  }

  private _mapJobEventProtoToProps(proto: JobEventProto): JobEventProps {
    const job = this.primeJob({ id: proto.getJobId() })
    const triggeredBy = proto.getTriggeredBy()
    return {
      ...mapJobEventProtoToProps(proto),
      job,
      triggeredBy: this._primeMember({
        userId: triggeredBy!.getUserId(),
        userPublicId: triggeredBy!.getUserPublicId(),
        workspaceId: job.workspace.id,
        givenName: triggeredBy!.getGivenName(),
        familyName: triggeredBy!.getFamilyName(),
      }),
    }
  }

  /**
   * Used by DataLoader.
   */
  private async _fetchJobs(ids: readonly string[]) {
    // The API has no batch get, so each job is fetched on its own.
    return Promise.all(
      ids.map(async (id) =>
        this._fetchJob(id).catch((err) => {
          this._jobLoader.clear(id)
          throw err
        }),
      ),
    )
  }

  /**
   * Used to load a job
   */
  private async _fetchJob(id: string): Promise<Job> {
    const request = new GetJobRequest().setId(id)
    const response = await this._api.jobs.getJob(request)
    return this._setJobProto(response)
  }

  /**
   * Refetches a job together with its events.
   */
  private async _updateJobEvents(id: Id) {
    const jobEventsPromise = this._api.jobEvents
      .listJobEvents(
        new ListJobEventsRequest().setJobId(Id.assert(id)).setPageSize(500),
      )
      .then((result) =>
        this.jobEvents.set(
          result
            .getJobEventsList()
            .map((x) => this._mapJobEventProtoToProps(x)),
        ),
      )

    const jobPromise = this._fetchJob(Id.assert(id))

    const [, job] = await Promise.all([jobEventsPromise, jobPromise])

    return job
  }

  /**
   * Handles an incoming job update event from DotNET API.
   *
   * @param realtimeEvent
   */
  private async _handleRemoteJobUpdate(
    realtimeEvent: RealtimeEvent,
  ): Promise<JobChangeEvent | undefined> {
    const isJobUpdatedEvent =
      realtimeEvent.getTypeCase() === RealtimeEvent.TypeCase.JOB_UPDATED

    const proto = isJobUpdatedEvent
      ? realtimeEvent.getJobUpdated()?.getJob()
      : realtimeEvent.getJobCreated()?.getJob()

    if (!proto) {
      return
    }

    const job = await this._mergeWithRemoteData(proto)
    if (proto.getArchived()) {
      this.jobs.remove(proto.getId())
      return { event: 'removed', data: job }
    }

    return { event: isJobUpdatedEvent ? 'patched' : 'created', data: job }
  }

  /**
   * Handles an incoming job recorded event from DotNET API.
   *
   * @param realtimeEvent
   */
  @action.bound
  private _handleRemoteJobEventRecorded(
    realtimeEvent: RealtimeEvent,
  ): JobEventProps | undefined {
    const jobEventProto = realtimeEvent.getJobEventRecorded()?.getJobEvent()

    if (!jobEventProto) {
      return
    }

    return this._mapJobEventProtoToProps(jobEventProto)
  }

  /**
   * Handles the realtime updates.
   *
   * @param proto
   */
  private async _mergeWithRemoteData(proto: JobProto): Promise<Job> {
    const job = this.jobs.get(proto.getId())
    if (!job) {
      return this._setJobProto(proto)
    }
    const eventDate = safeTimestampToDate(proto.getDateModified())
    if (job.dateModified < eventDate) {
      return this._setJobProto(proto)
    }

    return job
  }
}
